//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box } from '@mui/material';
import { visuallyHidden } from '@mui/utils';

import FloatingPanel from './FloatingPanel';
import { type PanelBounds, type PanelSize } from './panelGeometry';
import { panelElementId, panelLauncherId } from './panelIds';
import { type PanelEntry } from '@/contexts/PanelContext';
import { usePanels } from '@/hooks/usePanels';

// Panels sit above the app chrome -- the header and footer pin themselves to
// MUI's appBar tier (1100) -- and below its drawer (1200) and modal (1300): a
// panel is meant to cover the page, while the mobile navigation, a temporary
// Drawer whose modal root sits exactly at the drawer tier, and the Settings
// dialog are both meant to cover a panel. A panel painted over the drawer
// would be visible but unusable, held under the drawer's focus trap while
// hiding the navigation items beneath it. Named here rather than sprinkled
// through sx blocks.
export const PANEL_Z_BASE = 1110;
export const PANEL_Z_MAX = 1190;

// The id the panels skip link points at.
export const PANEL_REGION_ID = 'open-panels';

// A repeat of the same message leaves the live region's text unchanged, and an
// unchanged region is not read again -- reachable, since two panels may share
// a title. Alternating a non-breaking space makes the repeat a textual change
// without changing what is read out.
const REPEAT_SPACER = '\u00a0';

// What was last said, and how many times the region has been written. The
// count exists only to break textual identity between consecutive messages.
interface PanelAnnouncement {
  readonly text: string;
  readonly count: number;
}

// The previous state of each mounted panel. Iconifying keeps the entry, so a
// diff over ids alone cannot see the most common change of all.
interface KnownPanel {
  readonly title: string;
  readonly state: PanelEntry['state'];
}

// The viewport a fixed-position panel is laid out in. Its containing block is
// the initial containing block, which excludes a classic scrollbar --
// `innerWidth` counts one. Measured that way a right-anchored panel and its
// resize grip sit a scrollbar's width past the visible edge, and the clamp
// cannot pull them back because it reads the same number. Zero is a document
// with no layout, where `innerWidth` is the only measurement there is.
const viewportSize = (): PanelSize => ({
  w: document.documentElement.clientWidth || window.innerWidth,
  h: document.documentElement.clientHeight || window.innerHeight
});

/**
 * The one place panels are mounted.
 *
 * Rendered as a sibling of the router rather than inside a page, because a
 * route change would otherwise unmount every panel -- and unmounting a plugin
 * surface's iframe reloads its document.  Portaled to `document.body` so no
 * ancestor's stacking context or `overflow: hidden` can clip a panel, which is
 * the same reason the chat popup portaled before it.
 *
 * Bounds are measured from the live header rather than assumed, so the nav row
 * and the expiration banner are accounted for without this file knowing they
 * exist: a panel is placed below the header, not over it.  Only placement is
 * guaranteed, not that the region is always current -- a header whose height
 * changes without the observed elements changing size is seen at the next
 * observation, not at the moment it happens.
 */
const PanelHost = () => {
  const {
    panels, bounds, setBounds, iconifyPanel, movePanel,
    raisePanel, resizePanel, resetPanelGeometry, getPanelInvoker
  } = usePanels();
  const [announcement, setAnnouncement] =
    useState<PanelAnnouncement>({ text: '', count: 0 });
  const known = useRef(new Map<string, KnownPanel>());

  // Each panel's z tier, aligned with `panels`. Stacking is drawn with
  // zIndex from the entries' stackOrder rather than by rendering the list in
  // stacking order: a raise that reordered the children would move the
  // raised panel's DOM node, silently voiding the pointer capture its title
  // bar takes on the same press. Sorting a copy leaves the render order --
  // and so the DOM -- in mount order, untouched by any raise.
  const zTiers = useMemo(() => {
    const byStack = panels.toSorted((a, b) => a.stackOrder - b.stackOrder);
    return panels.map((entry) =>
      Math.min(PANEL_Z_BASE + byStack.indexOf(entry), PANEL_Z_MAX));
  }, [panels]);

  const measure = useCallback(() => {
    const view = viewportSize();
    // The whole viewport, header included. A panel is a window: where it may
    // sit is the user's business, and confining it below the chrome meant
    // every panel jumped down whenever the header grew -- an expiration
    // banner appearing would shove the lot of them.
    //
    // Reported unconditionally: `setBounds` itself is a no-op on a rect that
    // already matches the registry, so the layout root's `minHeight: 100vh`
    // triggering an observation on any page whose content grows costs
    // nothing to report here.
    const next: PanelBounds = {
      left: 0,
      top: 0,
      width: view.w,
      height: view.h
    };
    setBounds(next);
  }, [setBounds]);

  useEffect(() => {
    const watcher = new ResizeObserver(measure);
    watcher.observe(document.body);
    measure();
    window.addEventListener('resize', measure);
    return () => {
      watcher.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [measure]);

  // Re-measure whenever the set of mounted panels changes, so a measurement
  // taken before the window settled cannot strand a panel opened later.
  useEffect(() => {
    if (panels.length > 0) measure();
  }, [panels.length, measure]);

  // Announce what changed, comparing against the previous state rather than
  // hooking the lifecycle callbacks, which have no business knowing about
  // screen readers. Every change in one commit is said in one message: a
  // second write would discard the first, so an opening batched with a
  // closing would leave one of them unheard.
  useEffect(() => {
    const previous = known.current;
    const next = new Map<string, KnownPanel>();
    const messages: string[] = [];
    // Panels that stopped being on screen in this commit, iconified or
    // closed: the same diff answers both what to say and where focus goes.
    const dismissed: string[] = [];
    for (const { spec, state } of panels) {
      next.set(spec.id, { title: spec.title, state });
      const before = previous.get(spec.id);
      if (!before) messages.push(`${spec.title} panel opened`);
      else if (before.state !== state) {
        messages.push(state === 'open' ?
          `${spec.title} panel restored` : `${spec.title} panel iconified`);
        if (state === 'iconified') dismissed.push(spec.id);
      }
    }
    for (const [id, before] of previous) {
      if (next.has(id)) continue;
      messages.push(`${before.title} panel closed`);
      if (before.state === 'open') dismissed.push(id);
    }
    known.current = next;
    if (messages.length > 0) {
      setAnnouncement((prev) => ({
        text: messages.join('. '), count: prev.count + 1
      }));
    }

    // Hand focus back to whatever opened the panel that just went away. An
    // iconified panel is hidden rather than unmounted, so focus that was
    // inside it -- or that its options menu was about to restore into it --
    // is left with nowhere to be.
    //
    // Orphaned means one of two things: the body holds focus, which is where
    // a browser puts it once the element holding it stops being rendered, or
    // focus is still inside the panel that just went away, which is what an
    // engine that does not blur on hide leaves behind. Anything else is focus
    // the user placed themselves -- including focus inside another panel that
    // is still open -- and a dismissal is no reason to pull them out of it.
    const orphaned = document.activeElement === document.body ||
      dismissed.some((id) => document.getElementById(panelElementId(id))
        ?.contains(document.activeElement));
    // The invoker first, then the panel's launcher pill, then the main
    // landmark. The invoker is null more often than it sounds: a restore
    // records none, and a recorded one does not survive the header
    // remounting on a route change -- without the fallbacks, dismissing such
    // a panel drops a keyboard user back to the top of the document. A
    // disabled pill cannot take focus, so it is passed over rather than
    // focused into silence.
    if (dismissed.length > 0 && orphaned) {
      const launcher =
        document.getElementById(panelLauncherId(dismissed[0]));
      const target = getPanelInvoker(dismissed[0]) ??
        (launcher instanceof HTMLButtonElement && launcher.disabled ?
          null : launcher) ??
        document.querySelector<HTMLElement>('main');
      target?.focus();
    }
  }, [panels, getPanelInvoker]);

  return createPortal(
    <>
      {/* Outside the region below, and never unmounted with it: a live region
          is announced when its text changes, not when it arrives already
          holding text, so one that came and went with the panels would lose
          the first opening and the last closing -- the two changes it most
          needs to say. */}
      <Box role="status" aria-live="polite" sx={visuallyHidden}>
        {announcement.text}
        {REPEAT_SPACER.repeat(announcement.count % 2)}
      </Box>
      {/* Only once there is something in it. An empty landmark is still a
          landmark: it would sit in a screen reader's list on every page,
          offering to go somewhere that holds nothing. */}
      {panels.length > 0 && (
        <Box
          id={PANEL_REGION_ID}
          role="region"
          aria-label="Open panels"
          // The skip link's target: a fragment link scrolls to an element but
          // moves focus only to a focusable one.
          tabIndex={-1}
        >
          {panels.map((entry, index) => (
            <FloatingPanel
              key={entry.spec.id}
              entry={entry}
              bounds={bounds}
              zIndex={zTiers[index]}
              onMove={(x, y) => movePanel(entry.spec.id, x, y)}
              onResize={(rect) => resizePanel(entry.spec.id, rect)}
              onIconify={() => iconifyPanel(entry.spec.id)}
              onRaise={() => raisePanel(entry.spec.id)}
              onResetGeometry={() => resetPanelGeometry(entry.spec.id)}
            />
          ))}
        </Box>
      )}
    </>,
    document.body
  );
};

export default PanelHost;
